// pages/PayrollHistory.tsx
import React, { useState, useEffect } from "react";
import {
  Calendar,
  DollarSign,
  Users,
  Eye,
  FileText,
  Download,
  Search,
  ChevronLeft,
  ChevronRight,
  Clock,
} from "lucide-react";
// import { useAuth } from "@/hooks/useAuth";
import PayrollRunDetailsModal from "@/components/PayrollRunDetailsModal";
import { formatCurrency } from "@/utils/currency";
import { formatDate } from "@/utils/date";

interface PayrollRun {
  id: number;
  periodStart: string;
  periodEnd: string;
  status: "draft" | "processing" | "completed" | "cancelled";
  totalEmployees: number;
  totalGrossPay: number;
  totalNetPay: number;
  totalDeductions: number;
  processedByUser: {
    firstName: string;
    lastName: string;
    email?: string;
  };
  createdAt: string;
  payrollItems?: Array<{
    employeeName: string;
    grossSalary: number;
    netSalary: number;
    deductions: number;
  }>;
}

const PayrollHistory: React.FC = () => {
//   const { user } = useAuth();
  const [payrollRuns, setPayrollRuns] = useState<PayrollRun[]>([]);
  const [filteredRuns, setFilteredRuns] = useState<PayrollRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedPayrollRun, setSelectedPayrollRun] = useState<PayrollRun | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFilter, setDateFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const fetchPayrollHistory = async () => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`/api/v1/payroll/history?page=${page}&limit=${itemsPerPage}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const result = await response.json();
        setPayrollRuns(result.data.payrollRuns);
        setFilteredRuns(result.data.payrollRuns);
        setTotalPages(result.data.pagination.totalPages);
      }
    } catch (error) {
      console.error("Error fetching payroll history:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPayrollHistory();
  }, [page, itemsPerPage]);

  useEffect(() => {
    let filtered = payrollRuns;

    if (searchTerm) {
      filtered = filtered.filter(run =>
        run.processedByUser.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        run.processedByUser.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        run.status.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter(run => run.status === statusFilter);
    }

    if (dateFilter !== "all") {
      const now = new Date();
      const thirtyDaysAgo = new Date(now.setDate(now.getDate() - 30));

      filtered = filtered.filter(run => {
        const runDate = new Date(run.createdAt);
        if (dateFilter === "last30") return runDate >= thirtyDaysAgo;
        if (dateFilter === "last90") {
          const ninetyDaysAgo = new Date(now.setDate(now.getDate() - 90));
          return runDate >= ninetyDaysAgo;
        }
        return true;
      });
    }

    setFilteredRuns(filtered);
  }, [searchTerm, statusFilter, dateFilter, payrollRuns]);

  const handleViewDetails = (run: PayrollRun) => {
    setSelectedPayrollRun(run);
    setShowDetailsModal(true);
  };

  const handleExportCSV = async (runId: number) => {
    try {
      const token = localStorage.getItem("accessToken");
      const response = await fetch(`/api/v1/payroll/runs/${runId}/export`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `payroll-run-${runId}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Error exporting payroll:", error);
    }
  };

  const handleExportExcel = async () => {
    try {
      const token = localStorage.getItem("accessToken");

      const params = new URLSearchParams();
      if (searchTerm) params.append('search', searchTerm);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (dateFilter !== 'all') params.append('date', dateFilter);

      const response = await fetch(`/api/v1/payroll/history/export/excel?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;

        const contentType = response.headers.get('content-type');
        const isExcel = contentType?.includes('spreadsheet');
        const extension = isExcel ? 'xlsx' : 'csv';

        a.download = `payroll-history-${new Date().toISOString().split('T')[0]}.${extension}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        throw new Error('Export failed');
      }
    } catch (error) {
      console.error("Error exporting Excel:", error);
      generateExcelFallback();
    }
  };

  const generateExcelFallback = () => {
    const headers = [
      'Pay Period Start',
      'Pay Period End',
      'Status',
      'Employees',
      'Gross Pay',
      'Net Pay',
      'Deductions',
      'Processed By',
      'Created Date'
    ];

    const csvData = filteredRuns.map(run => [
      formatDate(run.periodStart),
      formatDate(run.periodEnd),
      run.status,
      run.totalEmployees.toString(),
      run.totalGrossPay.toString(),
      run.totalNetPay.toString(),
      run.totalDeductions.toString(),
      `${run.processedByUser.firstName} ${run.processedByUser.lastName}`,
      formatDate(run.createdAt)
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payroll-history-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  };

  const getStatusBadge = (status: PayrollRun["status"]) => {
    const map = {
      completed: { classes: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Completed" },
      cancelled: { classes: "bg-red-50 text-red-700 border-red-200", label: "Cancelled" },
      processing: { classes: "bg-amber-50 text-amber-700 border-amber-200", label: "Processing" },
      draft: { classes: "bg-indigo-50 text-indigo-700 border-indigo-200", label: "Draft" },
    };
    return map[status] || map.draft;
  };

  const totalProcessed = payrollRuns.filter(run => run.status === 'completed').length;
  const totalAmount = payrollRuns
    .filter(run => run.status === 'completed')
    .reduce((sum, run) => sum + run.totalNetPay, 0);

  const getVisiblePages = () => {
    const delta = 2;
    const range = [];
    const rangeWithDots = [];

    for (
      let i = Math.max(2, page - delta);
      i <= Math.min(totalPages - 1, page + delta);
      i++
    ) {
      range.push(i);
    }

    if (page - delta > 2) {
      rangeWithDots.push(1, '...');
    } else {
      rangeWithDots.push(1);
    }

    rangeWithDots.push(...range);

    if (page + delta < totalPages - 1) {
      rangeWithDots.push('...', totalPages);
    } else if (totalPages > 1) {
      rangeWithDots.push(totalPages);
    }

    return rangeWithDots;
  };

  return (
    <div className="max-w-7xl mx-auto space-y-5">
      {/* Page Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3 mb-4 lg:mb-0">
          <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
            <FileText size={20} className="text-indigo-600" />
          </div>
          <div>
            <h1 className="text-lg font-semibold text-gray-900 dark:text-white">Payroll History</h1>
            <p className="text-gray-500 dark:text-gray-400 text-xs">
              Complete history of all payroll runs and processed payments
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Total Processed</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm">
              {totalProcessed} runs
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg px-3 py-2 border border-gray-200 dark:border-gray-700">
            <div className="text-gray-500 dark:text-gray-400 text-xs mb-1">Total Amount</div>
            <div className="text-gray-900 dark:text-white font-bold text-sm">
              {formatCurrency(totalAmount, "USD")}
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Export Section */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card p-4">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Search and Filters */}
          <div className="flex flex-col lg:flex-row lg:items-center gap-4 flex-1">
            {/* Search */}
            <div className="flex-1 max-w-md">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by processor or status..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-9 pr-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
                />
              </div>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap gap-2">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
              >
                <option value="all">All Status</option>
                <option value="completed">Completed</option>
                <option value="processing">Processing</option>
                <option value="draft">Draft</option>
                <option value="cancelled">Cancelled</option>
              </select>

              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 focus:ring-2 focus:ring-indigo-600 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-xs"
              >
                <option value="all">All Time</option>
                <option value="last30">Last 30 Days</option>
                <option value="last90">Last 90 Days</option>
              </select>
            </div>
          </div>

          {/* Export Button */}
          <button
            onClick={handleExportExcel}
            className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition-all duration-200 hover:shadow-md text-xs"
          >
            <Download size={12} />
            Export Excel
          </button>
        </div>
      </div>

      {/* Payroll History Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 shadow-card overflow-hidden">
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
              <FileText size={14} />
              Payroll Run History
            </h2>
            <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 text-xs">
              <span>Show:</span>
              <select
                value={itemsPerPage}
                onChange={(e) => setItemsPerPage(Number(e.target.value))}
                className="bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded px-2 py-1 text-gray-900 dark:text-white text-xs focus:outline-none focus:ring-1 focus:ring-indigo-600"
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
              </select>
              <span>entries</span>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center items-center py-8">
            <div className="h-8 w-8 rounded-full border-[3px] border-indigo-600 border-t-transparent animate-spin" />
          </div>
        ) : filteredRuns.length === 0 ? (
          <div className="text-center py-8">
            <div className="h-12 w-12 mx-auto bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center mb-3">
              <FileText className="text-gray-400 h-5 w-5" />
            </div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              No Payroll Runs Found
            </h3>
            <p className="text-gray-600 dark:text-gray-300 text-xs mb-4">
              {searchTerm || statusFilter !== "all" || dateFilter !== "all"
                ? "Try adjusting your filters"
                : "No payroll runs have been processed yet"}
            </p>
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="data-table">
                <thead>
                  <tr>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[140px]">
                      Pay Period
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[80px]">
                      Status
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[80px]">
                      Employees
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[100px]">
                      Gross Pay
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[100px]">
                      Net Pay
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[100px]">
                      Deductions
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[120px]">
                      Processed By
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[100px]">
                      Date Created
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-900 dark:text-white uppercase tracking-wider text-xs min-w-[80px]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {filteredRuns.map((run) => {
                    const statusInfo = getStatusBadge(run.status);
                    return (
                      <tr key={run.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200">
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="h-6 w-6 rounded flex items-center justify-center bg-indigo-50 text-indigo-600">
                              <Calendar size={12} />
                            </div>
                            <div>
                              <p className="font-medium text-gray-900 dark:text-white text-xs">
                                {formatDate(run.periodStart)}
                              </p>
                              <p className="text-gray-500 dark:text-gray-400 text-xs flex items-center gap-1">
                                <Clock size={10} />
                                to {formatDate(run.periodEnd)}
                              </p>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium border ${statusInfo.classes}`}>
                            {statusInfo.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white text-xs">
                          {run.totalEmployees}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white text-xs">
                          {formatCurrency(run.totalGrossPay, "USD")}
                        </td>
                        <td className="px-4 py-3 font-medium text-xs text-emerald-700">
                          {formatCurrency(run.totalNetPay, "USD")}
                        </td>
                        <td className="px-4 py-3 font-medium text-gray-900 dark:text-white text-xs">
                          {formatCurrency(run.totalDeductions, "USD")}
                        </td>
                        <td className="px-4 py-3 text-gray-900 dark:text-white font-medium text-xs">
                          {run.processedByUser.firstName} {run.processedByUser.lastName}
                        </td>
                        <td className="px-4 py-3 text-gray-600 dark:text-gray-400 text-xs">
                          {formatDate(run.createdAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleViewDetails(run)}
                              className="text-gray-400 hover:text-indigo-600 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                              title="View Details"
                            >
                              <Eye size={12} />
                            </button>
                            {run.status === 'completed' && (
                              <button
                                onClick={() => handleExportCSV(run.id)}
                                className="text-gray-400 hover:text-emerald-700 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                                title="Export CSV"
                              >
                                <Download size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="text-xs text-gray-600 dark:text-gray-400 font-medium">
                    Showing {((page - 1) * itemsPerPage) + 1} to {Math.min(page * itemsPerPage, filteredRuns.length)} of {filteredRuns.length} entries
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => setPage(1)}
                      disabled={page === 1}
                      className="p-1.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-300"
                      title="First Page"
                    >
                      <ChevronLeft className="h-3 w-3" />
                    </button>

                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="px-2 py-1 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-300"
                    >
                      Previous
                    </button>

                    <div className="flex items-center gap-1">
                      {getVisiblePages().map((pageNum, index) => (
                        <button
                          key={index}
                          onClick={() => typeof pageNum === 'number' ? setPage(pageNum) : null}
                          className={`px-2 py-1 text-xs font-medium rounded transition-colors min-w-[32px] ${
                            page === pageNum
                              ? 'bg-indigo-600 text-white'
                              : typeof pageNum === 'number'
                              ? 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-600'
                              : 'text-gray-400 cursor-default'
                          }`}
                          disabled={typeof pageNum !== 'number'}
                        >
                          {pageNum}
                        </button>
                      ))}
                    </div>

                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="px-2 py-1 text-xs font-medium bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-300"
                    >
                      Next
                    </button>

                    <button
                      onClick={() => setPage(totalPages)}
                      disabled={page === totalPages}
                      className="p-1.5 rounded bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-gray-700 dark:text-gray-300"
                      title="Last Page"
                    >
                      <ChevronRight className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <DollarSign size={14} className="text-indigo-600" />
            Total Processed
          </h3>
          <p className="text-base font-bold text-gray-900 dark:text-white mb-1">
            {formatCurrency(totalAmount, "USD")}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Across all completed payroll runs
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <Users size={14} className="text-emerald-600" />
            Employees Covered
          </h3>
          <p className="text-base font-bold text-gray-900 dark:text-white mb-1">
            {payrollRuns.reduce((max, run) => Math.max(max, run.totalEmployees), 0)}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Maximum in a single run
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-card">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2 flex items-center gap-2">
            <FileText size={14} className="text-indigo-600" />
            Success Rate
          </h3>
          <p className="text-base font-bold text-gray-900 dark:text-white mb-1">
            {totalProcessed > 0 ? Math.round((totalProcessed / payrollRuns.length) * 100) : 0}%
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Completed runs
          </p>
        </div>
      </div>

      {/* Details Modal */}
      {showDetailsModal && selectedPayrollRun && (
        <PayrollRunDetailsModal
          payrollRun={selectedPayrollRun}
          onClose={() => setShowDetailsModal(false)}
          onApproved={fetchPayrollHistory}
        />
      )}
    </div>
  );
};

export default PayrollHistory;
